import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';

import { createUser, formErrorsSelector, setFormErrors } from 'src/store/auth';
import { setBreadcrumbsList } from 'src/store/breadcrumbs';

import { Button, CustomLink, Input, PhoneInput, Tabs } from 'src/components';

import { ROUTE, BREADCRUMBS_DATA } from 'src/constants';

import {
  ArrowRightBottomIcon,
  BackgroundImage,
  EllipseImage,
  PortraitImage,
  TableImage,
} from 'src/assets';
import './signUp.scss';

export const SignUp = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const formErrors = useSelector(formErrorsSelector);

  const options = [
    { to: ROUTE.AUTH.SIGNUP, label: t('signup:signup'), is_active: true },
    { to: ROUTE.AUTH.SIGNIN, label: t('signup:signin'), is_active: false },
  ];

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    password_confirmation: '',
  });

  useEffect(() => {
    dispatch(setBreadcrumbsList(BREADCRUMBS_DATA.ENTER));

    return () => {
      dispatch(setFormErrors({}));
    };
  }, [dispatch]);

  const onChangeInput = useCallback(
    (target) => (event) => {
      setFormData((prev) => ({ ...prev, [target]: event.target.value }));

      const copiedErrors = _.cloneDeep(formErrors);

      delete copiedErrors?.[target];

      dispatch(setFormErrors(copiedErrors));
    },
    [dispatch, formErrors],
  );

  const onSubmit = useCallback(
    (event) => {
      event.preventDefault();

      dispatch(createUser({ navigate, payload: { ...formData, phone: `+${formData.phone}` } }));
    },
    [dispatch, navigate, formData],
  );

  return (
    <div className="sign-up">
      <div className="sign-up-content">
        <div className="sign-up-tabs">
          <Tabs options={options} />
        </div>
        <div>
          <div className="sign-up-title">
            <h1>{t('signup:signup')}</h1>
            <div className="sign-up-subtitle">{t('signup:subTitle')}</div>
          </div>

          <form onSubmit={onSubmit} className="sign-up-form">
            <div className="sign-up-form-content">
              <Input
                label={t('form:firstName')}
                value={formData.first_name}
                onChange={onChangeInput('first_name')}
                errorMessage={formErrors?.first_name}
              />
              <Input
                label={t('form:lastName')}
                value={formData.last_name}
                onChange={onChangeInput('last_name')}
                errorMessage={formErrors?.last_name}
              />
              {/* <Input
                label={t('form:phone')}
                value={formData.phone}
                onChange={onChangeInput('phone')}
                errorMessage={formErrors?.phone}
              /> */}
              <PhoneInput
                label={t('form:phone')}
                value={formData.phone}
                onChange={onChangeInput('phone')}
                errorMessage={formErrors?.phone}
              />
              <Input
                label={t('form:email')}
                value={formData.email}
                type="email"
                onChange={onChangeInput('email')}
                errorMessage={formErrors?.email}
              />
              <Input
                label={t('form:password')}
                value={formData.password}
                onChange={onChangeInput('password')}
                errorMessage={formErrors?.password}
                type="password"
              />
              <Input
                label={t('form:confirmedPassword')}
                value={formData.password_confirmation}
                onChange={onChangeInput('password_confirmation')}
                errorMessage={formErrors?.password_confirmation}
                type="password"
              />
            </div>
            <div className="checkbox">
              <input type="checkbox" required />
              <span>
                {t('form:registering')}{' '}
                <CustomLink
                  title={t('form:usage')}
                  to={ROUTE.CLIENT_AGREEMENT.replace(':language', i18n.language)}
                />{' '}
                {t('form:and')}{' '}
                <CustomLink
                  title={t('form:politics')}
                  to={ROUTE.PRIVACY_POLICY.replace(':language', i18n.language)}
                />
              </span>
            </div>
            <div className="sign-up-form-button">
              <Button title={t('signup:register')} icon={ArrowRightBottomIcon} />
            </div>
          </form>
        </div>

        <div className="images-container">
          <div className="images-wrapper">
            <img src={BackgroundImage} alt={BackgroundImage} className="images-background image" />
            <img src={PortraitImage} alt={PortraitImage} className="images-portrait image" />
            <img src={TableImage} alt={TableImage} className="images-tablet image" />
            <img src={EllipseImage} alt={EllipseImage} className="images-ellipse1 image" />
            <img src={EllipseImage} alt={EllipseImage} className="images-ellipse2 image" />
          </div>
        </div>
      </div>
    </div>
  );
};
